import React from 'react';
import {
  Box,
  Typography,
  Switch,
  Alert,
  AlertTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { usePrivacyConsent } from '../../hooks/usePrivacyConsent';

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
}));

const Card = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',
  border: '1px solid rgba(0,0,0,0.06)',
  width: '100%',
  maxWidth: 480,
  overflow: 'hidden',
});

const CardHeader = styled(Box)({
  background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
  padding: '28px 32px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});

const CardBody = styled(Box)({
  padding: '32px',
});

const PreferenceRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  padding: '20px',
  borderRadius: '10px',
  border: '1px solid #F3F4F6',
  backgroundColor: '#FAFAFA',
});

const PurpleSwitch = styled(Switch)({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#4F46E5',
    '&:hover': { backgroundColor: 'rgba(79, 70, 229, 0.08)' },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#4F46E5',
  },
});

const ManagePreferences = () => {
  const { analyticsAllowed, applyConsent } = usePrivacyConsent();

  const handleAnalyticsToggle = async (event) => {
    await applyConsent({
      analyticsAllowed: event.target.checked,
      gpcEnforced: false,
    });
  };

  return (
    <PageWrapper>
      <Card>
        <CardHeader>
          <Box sx={{
            width: 44, height: 44, borderRadius: '10px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <PrivacyTipOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
              Privacy Preferences
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
              Manage cookies and tracking settings
            </Typography>
          </Box>
        </CardHeader>

        <CardBody>
          {navigator.globalPrivacyControl && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
              <AlertTitle>Global Privacy Control Detected</AlertTitle>
              In accordance with your browser settings, we've automatically
              disabled non-essential cookies.
            </Alert>
          )}

          <PreferenceRow>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '8px',
                backgroundColor: analyticsAllowed ? '#EEF2FF' : '#F3F4F6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background-color 0.2s',
              }}>
                <BarChartOutlinedIcon sx={{
                  fontSize: 20,
                  color: analyticsAllowed ? '#4F46E5' : '#9CA3AF',
                  transition: 'color 0.2s',
                }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="#111827">
                  Analytics Cookies
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                  Help us understand how you use the platform so we can improve
                  your experience.
                </Typography>
                <Typography variant="caption" sx={{
                  mt: 1, display: 'inline-block',
                  color: analyticsAllowed ? '#059669' : '#9CA3AF',
                  fontWeight: 500,
                  transition: 'color 0.2s',
                }}>
                  {analyticsAllowed ? 'Enabled' : 'Disabled'}
                </Typography>
              </Box>
            </Box>
            <PurpleSwitch
              checked={analyticsAllowed}
              onChange={handleAnalyticsToggle}
              inputProps={{ 'aria-label': 'Toggle analytics cookies' }}
            />
          </PreferenceRow>

          <Typography variant="caption" color="text.disabled" sx={{ mt: 3, display: 'block', lineHeight: 1.6 }}>
            Essential cookies required for the platform to function are always
            active and cannot be disabled. Changes take effect immediately.
          </Typography>
        </CardBody>
      </Card>
    </PageWrapper>
  );
};

export default ManagePreferences;
